/**
 * Client-side mirror of backend/src/game/dungeon-prototype/config/tuning.ts.
 * This project has no shared package between backend/ and frontend/, so
 * these values are duplicated intentionally (movement-netcode.md Core Rule
 * #4 requires the client to run the same movement code as the server).
 * Keep both files in sync by hand.
 */

export const NET = {
  BROADCAST_HZ: 20,
  CLOCK_EWMA_ALPHA: 0.2,
  CLOCK_PING_INTERVAL_MS: 2000,
  CLOCK_PING_BURST_COUNT: 5,
  CLOCK_PING_BURST_INTERVAL_MS: 150,
  SNAP_THRESHOLD_M: 0.3,
  CORRECTION_SMOOTH_MS: 150,
  JITTER_BUFFER_MS: 30,
  /** camera-and-controls.md amendment (2026-08-20) — see the backend tuning.ts copy for the full rationale. */
  WALK_SPEED_MPS: 2.2,
  SPRINT_SPEED_MPS: 5.6,
  CROUCH_SPEED_MPS: 1.4,
} as const;

/*
 * There are deliberately NO `HEALTH_MAX` / `STAMINA_MAX` mirrors here.
 *
 * They existed for one commit and produced a HUD reading "110 / 100": the
 * knight's `fortitude` talent adds 10 HP per rank, so a player's real maximum
 * is per-player and a mirrored base constant is the wrong denominator by
 * construction. `PlayerState.hpMax`/`staminaMax` and `MonsterState.hpMax` are
 * synced instead — ask the entity, never a constant.
 */

/**
 * combat-depth.md (2026-08-21) adds BLOCKING and FEINT_RECOVERY. It also added
 * a BANDAGING channel, removed 2026-08-25 along with bleeding.
 *
 * `CASTING` (2026-08-24) is a catch-up: the MMO combat slice added it to the
 * server's own `EntityStateName` and never to this mirror. The consequence was
 * live, not theoretical — `animGroupOf` ends with `return state as AnimGroup`,
 * so a casting druid resolved to the group 'CASTING', which no clip is
 * registered under, and the model held whatever it had been doing. The
 * comparison at the cast-bar call site compiled anyway because a Colyseus
 * schema field is typed `string`, which is exactly why nothing caught it.
 */
export type EntityStateName =
  | 'IDLE' | 'MOVING' | 'SPRINTING' | 'CROUCHING' | 'BLOCKING'
  | 'ATTACK_WINDUP' | 'ATTACK_ACTIVE' | 'ATTACK_RECOVERY' | 'FEINT_RECOVERY'
  | 'PARRY_WINDOW' | 'PARRY_RECOVERY' | 'CASTING' | 'STAGGERED' | 'DEAD' | 'DISCONNECTED';
